/* eslint-disable @typescript-eslint/indent */
import {createAsyncThunk, createSlice, createAction, type PayloadAction} from '@reduxjs/toolkit'
import {
    getStationList,
    getCarrierList,
    getProductList,
    getDefaultAccount,
    getAllCarrierList,
    getBranchDetails,
    getEmbargoedCountryYN,
    getStatesByCountry,
    getAllCountriesList,
    getAllContinentsList,
    getAllRegionsList,
    getAllCountryCodeList,
    getPhoneCodeList,
    getAdminEmails
} from 'services/common.service'
import {type ICarrierAdminUserManagementAuthorizationConfig} from 'shared/Header/utils/CarrierAdminUserManagement'
import {type ICarrierAdminToolsAuthorizationConfig} from 'shared/Header/utils/CarrierAdminTools'
import {type IBranchAdminUserAuthorizationConfig} from 'shared/Header/utils/BranchUserManagement'
import {type IBranchToolsAuthorizationConfig} from 'shared/Header/utils/BranchTools'
import {type IServiceAdminUserAuthorizationConfig} from 'shared/Header/utils/ServiceAdminUserManagement'
import {type IServiceAdminToolsAuthorizationConfig} from 'shared/Header/utils/ServiceAdminUserTools'
import {convertUserAccessLevelToBoolean} from 'utils/helperFunctions'

export interface IUserAccessLevels {
    authorizationLevels: Record<string, unknown>
    pageLevelAccess: Record<string, unknown>
    userManagementAccess:
        | IBranchAdminUserAuthorizationConfig
        | ICarrierAdminUserManagementAuthorizationConfig
        | IServiceAdminUserAuthorizationConfig
        | Record<string, unknown>
    userToolsAccess:
        | IBranchToolsAuthorizationConfig
        | ICarrierAdminToolsAuthorizationConfig
        | IServiceAdminToolsAuthorizationConfig
        | Record<string, unknown>
    isMemberStatus?: boolean
    isColoader?: boolean
    isGSAStatus?: boolean
}

const defaultUserAccessLevels = {
    authorizationLevels: {},
    pageLevelAccess: {},
    userManagementAccess: {},
    userToolsAccess: {},
    isMemberStatus: false,
    isColoader: false,
    isGSAStatus: false
}

export interface IPhoneCountryCode {
    country: string
    countryCode: string
    countryPhone: string
}

export interface ICountryCode {
    country: any
    value: string
    label: string
    countryCode: string
    isStateAvailable: number
}
export interface stations {
    station: any
    isLoading: boolean
    error: any
    carrier: any
    carrierForCheck: any
    allCarriers: any[]
    branchDetails: any
    product: any
    defaultAccount: any
    defaultAccountFlag: boolean
    embargoedCountryYN: boolean
    userAccessLevels: IUserAccessLevels
    stateList: any[]
    countries: any[]
    continents: any[]
    regions: any[]
    AdminEmails: any[]
    allCountryCodes: ICountryCode[]
    allPhoneCodes: IPhoneCountryCode[]
    isBackdropPageLoading: boolean
}

const initialState: stations = {
    isLoading: false,
    station: [],
    error: null,
    carrier: [],
    carrierForCheck: [],
    allCarriers: [],
    branchDetails: null,
    product: [],
    defaultAccount: null,
    defaultAccountFlag: false,
    embargoedCountryYN: false,
    userAccessLevels: defaultUserAccessLevels,
    AdminEmails: [],
    stateList: [],
    countries: [],
    continents: [],
    regions: [],
    allCountryCodes: [],
    allPhoneCodes: [],
    isBackdropPageLoading: false
}

const throwError = (data) => {
    if (!data.successFlag || typeof data === 'string') {
        throw Error(String(data.errorList).match(/text=(.*?)(?=\])/)?.[1] ?? 'Something went wrong')
    }
}

export const fetchStationList = createAsyncThunk('common/fetchStationList', async (param: any) => {
    try {
        const {data} = await getStationList(param)
        return data.response
    } catch (error: any) {
        return error.message
    }
})

export const fetchAllCarriers = createAsyncThunk('common/fetchAllCarriers', async (_, {rejectWithValue}) => {
    try {
        const {data} = await getAllCarrierList()
        return data.response
    } catch (error: any) {
        return rejectWithValue(error.message)
    }
})

export const fetchBranchDetails = createAsyncThunk(
    'common/fetchBranchDetails',
    async (input: string, {rejectWithValue}) => {
        try {
            const {data} = await getBranchDetails(input)
            throwError(data)
            if (data.successFlag) {
                if (data.response !== null) {
                    return {
                        ...data.response,
                        displayName: String(data.response.branchName) + ' (' + String(data.response.station) + ')'
                    }
                } else {
                    return {
                        displayName: null
                    }
                }
            }
            return data.response
        } catch (err: any) {
            if (err.status >= 400 && err.status < 500) {
                const msg: string =
                    String(err?.response?.errors[0]?.field) + ' ' + String(err?.response?.errors[0]?.defaultMessage)
                return rejectWithValue(msg)
            } else if (err.status > 500) {
                return rejectWithValue('Something went wrong')
            }
            return rejectWithValue(err.message)
        }
    }
)
export const fetchAdminEmails = createAsyncThunk(
    'common/fetchAdminEmails',
    async (input: string, {rejectWithValue}) => {
        try {
            const {data} = await getAdminEmails(input)
            throwError(data)
            if (data.successFlag) {
                if (data.response !== null) {
                    return data.response
                } else {
                    return data
                }
            }
            return data.response
        } catch (err: any) {
            if (err.status >= 400 && err.status < 500) {
                const msg: string =
                    String(err?.response?.errors[0]?.field) + ' ' + String(err?.response?.errors[0]?.defaultMessage)
                return rejectWithValue(msg)
            } else if (err.status > 500) {
                return rejectWithValue('Something went wrong')
            }
            return rejectWithValue(err.message)
        }
    }
)

export const fetchCarrierList = createAsyncThunk('common/fetchCarrierList', async (payload: any, {rejectWithValue}) => {
    try {
        const {data} = await getCarrierList(payload)
        return data.response
    } catch (error: any) {
        return rejectWithValue(error.message)
    }
})

export const fetchCarrierListForCheck = createAsyncThunk(
    'common/fetchCarrierListForCheck',
    async (payload: any, {rejectWithValue}) => {
        try {
            const {data} = await getCarrierList(payload)
            return data.response
        } catch (error: any) {
            return rejectWithValue(error.message)
        }
    }
)

export const fetchDefaultAccounts = createAsyncThunk('common/fetchDefaultAccounts', async (payload: any) => {
    try {
        const {data} = await getDefaultAccount(payload)
        const result: any = []
        data.response.forEach((element) => {
            result[element.carrier] = element
        })
        return result
    } catch (error: any) {
        return error.message
    }
})

export const fetchProductList = createAsyncThunk('common/fetchProductList', async () => {
    try {
        const {data} = await getProductList()
        return data.response
    } catch (error: any) {
        return error.message
    }
})

export const fetchEmbargoedCountryYN = createAsyncThunk('common/fetchEmbargoedCountryYN', async () => {
    try {
        const {data} = await getEmbargoedCountryYN()
        return data.response
    } catch (error: any) {
        return error.message
    }
})

export const fetchStateList = createAsyncThunk('common/fetchStateList', async (param: string) => {
    try {
        const {data} = await getStatesByCountry(param)
        return data.response
    } catch (error: any) {
        return error.message
    }
})

export const fetchStateLists = createAsyncThunk('common/fetchStateList', async (param: string) => {
    try {
        const {data} = await getStatesByCountry(param)
        return data.response.map((data) => ({
            value: data.state,
            label: data.state
        }))
    } catch (error: any) {
        return error.message
    }
})

export const fetchDefaultAccount = createAction<boolean>('common/setDefaultAccountFlag')

export const fetchCountriesList = createAsyncThunk(
    'common/fetchCountriesList',
    async (payload: any, {rejectWithValue}) => {
        try {
            const {data} = await getAllCountriesList()
            return data.response
        } catch (error: any) {
            return rejectWithValue(error.message)
        }
    }
)

export const fetchContinentsList = createAsyncThunk(
    'common/fetchContinentsList',
    async (payload: any, {rejectWithValue}) => {
        try {
            const {data} = await getAllContinentsList()
            return data.response
        } catch (error: any) {
            return rejectWithValue(error.message)
        }
    }
)

export const fetchRegionsList = createAsyncThunk('common/fetchRegionsList', async (payload: any, {rejectWithValue}) => {
    try {
        const {data} = await getAllRegionsList()
        return data.response
    } catch (error: any) {
        return rejectWithValue(error.message)
    }
})

export const fetchAllCountryCodeList = createAsyncThunk(
    'common/fetchAllCountryCodeList',
    async (input: string, {rejectWithValue}) => {
        try {
            const {data} = await getAllCountryCodeList(input)
            if (data?.response.length) {
                const countryObjList = data.response?.filter(
                    (obj: Record<string, string>, index: number) =>
                        index === data.response?.findIndex((o: Record<string, string>) => obj.country === o.country)
                )
                const countryList = countryObjList.map((country: Record<string, string>) => ({
                    value: country.countryCode,
                    label: country.country,
                    countryCode: country.countryCode,
                    isStateAvailable: country.isStatesAvailable
                }))
                return countryList.sort((a, b) => a.label.localeCompare(b.label))
            }
            return []
        } catch (error: any) {
            return rejectWithValue(error.message)
        }
    }
)

export const fetchAllPhoneCodeList = createAsyncThunk('common/fetchAllPhoneCodeList', async (_, {rejectWithValue}) => {
    try {
        const {data} = await getPhoneCodeList()
        if (data?.response.length) return data.response
        return []
    } catch (error: any) {
        return rejectWithValue(error.message)
    }
})

export const commonSlice = createSlice({
    name: 'commonList',
    initialState,
    reducers: {
        resetCommonState: (state) => initialState,
        branchDetailsReset: (state) => ({
            ...state,
            branchDetails: null
        }),
        setUserAccessLevel: (state, action) => {
            const updatedAccess = convertUserAccessLevelToBoolean({...action.payload})
            state.userAccessLevels = {
                ...state.userAccessLevels,
                ...updatedAccess,
                authorizationLevels: {
                    ...state.userAccessLevels.authorizationLevels,
                    ...updatedAccess?.authorizationLevels
                },
                pageLevelAccess: {
                    ...state.userAccessLevels.pageLevelAccess,
                    ...updatedAccess?.pageLevelAccess
                },
                userManagementAccess: {
                    ...state.userAccessLevels.userManagementAccess,
                    ...updatedAccess?.userManagementAccess
                },
                userToolsAccess: {
                    ...state.userAccessLevels.userToolsAccess,
                    ...updatedAccess?.userToolsAccess
                }
            }
        },
        setBackdropPageLoading: (state, action: PayloadAction<boolean>) => {
            state.isBackdropPageLoading = action.payload
        }
    },
    extraReducers: function (builder) {
        builder.addCase(fetchAllCarriers.pending, (state, action) => {
            state.isLoading = true
        })
        builder.addCase(fetchAllCarriers.fulfilled, (state, action) => {
            state.isLoading = false
            state.allCarriers = action.payload
            state.error = null
        })
        builder.addCase(fetchAllCarriers.rejected, (state, action) => {
            state.isLoading = false
            state.allCarriers = []
            state.error = action.payload
        })
        builder.addCase(fetchBranchDetails.pending, (state, action) => {
            state.isLoading = true
        })
        builder.addCase(fetchBranchDetails.fulfilled, (state, action) => {
            state.isLoading = false
            state.branchDetails = action.payload
            state.error = null
        })
        builder.addCase(fetchBranchDetails.rejected, (state, action) => {
            state.isLoading = false
            state.branchDetails = null
            state.error = action.payload
        })
        builder.addCase(fetchAdminEmails.pending, (state, action) => {
            state.isLoading = true
        })
        builder.addCase(fetchAdminEmails.fulfilled, (state, action) => {
            state.isLoading = false
            state.AdminEmails = action.payload
            state.error = null
        })
        builder.addCase(fetchAdminEmails.rejected, (state, action) => {
            state.isLoading = false
            state.AdminEmails = []
            state.error = action.payload
        })
        builder.addCase(fetchCarrierList.pending, (state, action) => {
            state.isLoading = true
        })
        builder.addCase(fetchCarrierList.fulfilled, (state, action) => {
            state.isLoading = false
            state.carrier = action.payload
            state.error = null
        })
        builder.addCase(fetchCarrierList.rejected, (state, action) => {
            state.isLoading = false
            state.carrier = []
            state.error = action.payload
        })
        builder.addCase(fetchCarrierListForCheck.pending, (state, action) => {
            state.isLoading = true
        })
        builder.addCase(fetchCarrierListForCheck.fulfilled, (state, action) => {
            state.isLoading = false
            state.carrierForCheck = action.payload
            state.error = null
        })
        builder.addCase(fetchCarrierListForCheck.rejected, (state, action) => {
            state.isLoading = false
            state.carrierForCheck = []
            state.error = action.payload
        })
        builder.addCase(fetchProductList.pending, (state, action) => {
            state.isLoading = true
        })
        builder.addCase(fetchProductList.fulfilled, (state, action) => {
            state.isLoading = false
            state.product = action.payload
            state.error = null
        })
        builder.addCase(fetchProductList.rejected, (state, action) => {
            state.isLoading = false
            state.product = []
            state.error = action.payload
        })
        builder.addCase(fetchDefaultAccounts.pending, (state, action) => {
            state.isLoading = true
        })
        builder.addCase(fetchDefaultAccounts.fulfilled, (state, action) => {
            state.isLoading = false
            state.defaultAccount = action.payload
            state.error = null
        })
        builder.addCase(fetchDefaultAccounts.rejected, (state, action) => {
            state.isLoading = false
            state.defaultAccount = []
            state.error = action.payload
        })
        builder.addCase(fetchDefaultAccount, (state, action) => {
            state.defaultAccountFlag = action.payload
        })
        builder.addCase(fetchEmbargoedCountryYN.pending, (state, action) => {
            state.isLoading = true
        })
        builder.addCase(fetchEmbargoedCountryYN.fulfilled, (state, action) => {
            state.isLoading = false
            state.embargoedCountryYN = action.payload
            state.error = null
        })
        builder.addCase(fetchEmbargoedCountryYN.rejected, (state, action) => {
            state.isLoading = false
            state.embargoedCountryYN = false
            state.error = action.payload
        })
        builder.addCase(fetchStateList.pending, (state, action) => {
            state.isLoading = true
        })
        builder.addCase(fetchStateList.fulfilled, (state, action) => {
            state.isLoading = false
            state.stateList = action.payload
            state.error = null
        })
        builder.addCase(fetchStateList.rejected, (state, action) => {
            state.isLoading = false
            state.stateList = []
            state.error = action.payload
        })
        builder.addCase(fetchCountriesList.pending, (state, action) => {
            state.isLoading = true
        })
        builder.addCase(fetchCountriesList.fulfilled, (state, action) => {
            state.isLoading = false
            state.countries = action.payload
            state.error = null
        })
        builder.addCase(fetchCountriesList.rejected, (state, action) => {
            state.isLoading = false
            state.countries = []
            state.error = action.payload
        })
        builder.addCase(fetchContinentsList.pending, (state, action) => {
            state.isLoading = true
        })
        builder.addCase(fetchContinentsList.fulfilled, (state, action) => {
            state.isLoading = false
            state.continents = action.payload
            state.error = null
        })
        builder.addCase(fetchContinentsList.rejected, (state, action) => {
            state.isLoading = false
            state.continents = []
            state.error = action.payload
        })
        builder.addCase(fetchRegionsList.pending, (state, action) => {
            state.isLoading = true
        })
        builder.addCase(fetchRegionsList.fulfilled, (state, action) => {
            state.isLoading = false
            state.regions = action.payload
            state.error = null
        })
        builder.addCase(fetchRegionsList.rejected, (state, action) => {
            state.isLoading = false
            state.regions = []
            state.error = action.payload
        })
        builder.addCase(fetchAllCountryCodeList.pending, (state, action) => {
            state.isLoading = true
        })
        builder.addCase(fetchAllCountryCodeList.fulfilled, (state, action) => {
            state.isLoading = false
            state.allCountryCodes = action.payload
            state.error = null
        })
        builder.addCase(fetchAllCountryCodeList.rejected, (state, action) => {
            state.isLoading = false
            state.allCountryCodes = []
            state.error = action.payload
        })
        builder.addCase(fetchAllPhoneCodeList.pending, (state, action) => {
            state.isLoading = true
        })
        builder.addCase(fetchAllPhoneCodeList.fulfilled, (state, action) => {
            state.isLoading = false
            state.allPhoneCodes = action.payload
            state.error = null
        })
        builder.addCase(fetchAllPhoneCodeList.rejected, (state, action) => {
            state.isLoading = false
            state.allPhoneCodes = []
            state.error = action.payload
        })
    }
})

export const {resetCommonState, branchDetailsReset, setUserAccessLevel, setBackdropPageLoading} = commonSlice.actions

export const {reducer: commonReducer} = commonSlice
